import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  useNetworkData,
  friendlyLabel,
  findElementByName,
  HOW_UNLABELED,
  DIM_COMBINED,
  RawNomEdge,
} from "../hooks/useNetworkData";
import { buildEdgeColorScale } from "../utils/edgeColors";
import { findNetworkQuestionGroup } from "../utils/networkQuestionGroups";

type Mode = "perHow" | "aggregate" | "rawNomination";
type SortKey = "id" | "inDegree" | "outDegree" | "degree" | "betweenness" | "closeness";

type Edge = { sid: string; tid: string; how?: string; w: number };

function projectNominationValues(n: RawNomEdge, dim: string): string[] {
  if (dim === DIM_COMBINED) return n.hows || [];
  return n.fieldVals?.[dim] || [];
}

function computeEdges(
  rawEdges: RawNomEdge[],
  mode: Mode,
  allowedHows: Set<string>,
  selectedDim: string
): { edges: Edge[]; nodes: string[] } {
  const filtered = rawEdges;
  const nodeSet = new Set<string>();

  if (mode === "aggregate") {
    const map = new Map<string, number>();
    filtered.forEach((e) => {
      const key = `${e.a}→${e.b}`;
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    const edges: Edge[] = [];
    map.forEach((w, key) => {
      const [sid, tid] = key.split("→");
      nodeSet.add(sid);
      nodeSet.add(tid);
      edges.push({ sid, tid, w });
    });
    return { edges, nodes: Array.from(nodeSet) };
  }

  if (mode === "perHow") {
    const map = new Map<string, number>();
    filtered.forEach((e) => {
      const vals = projectNominationValues(e, selectedDim);
      const hows: string[] = (vals && vals.length ? vals : [HOW_UNLABELED]);
      const filteredHows = hows.filter((h: string) => allowedHows.has(h));
      filteredHows.forEach((h: string) => {
        const key = `${e.a}→${e.b}→${h}`;
        map.set(key, (map.get(key) ?? 0) + 1);
      });
    });
    const edges: Edge[] = [];
    map.forEach((w, key) => {
      const [sid, tid, how] = key.split("→");
      nodeSet.add(sid);
      nodeSet.add(tid);
      edges.push({ sid, tid, how, w });
    });
    return { edges, nodes: Array.from(nodeSet) };
  }

  // raw nomination
  const edges: Edge[] = filtered.map((e) => {
    nodeSet.add(e.a);
    nodeSet.add(e.b);
    return { sid: e.a, tid: e.b, w: 1 };
  });
  return { edges, nodes: Array.from(nodeSet) };
}

type MetricRow = {
  id: string;
  inDegree: number;
  outDegree: number;
  degree: number;
  betweenness: number;
  closeness: number;
};

function computeDegrees(nodes: string[], edges: Edge[]) {
  const metrics = new Map<string, MetricRow>();
  nodes.forEach((id) => metrics.set(id, { id, inDegree: 0, outDegree: 0, degree: 0, betweenness: 0, closeness: 0 }));
  edges.forEach((e) => {
    const out = metrics.get(e.sid);
    const inn = metrics.get(e.tid);
    if (out) {
      out.outDegree += e.w;
      out.degree += e.w;
    }
    if (inn) {
      inn.inDegree += e.w;
      inn.degree += e.w;
    }
  });
  return metrics;
}

// Simple unweighted betweenness (Brandes) treating edges as directed
function computeBetweenness(nodes: string[], edges: Edge[], metrics: Map<string, any>) {
  const adj = new Map<string, string[]>();
  nodes.forEach((n) => adj.set(n, []));
  edges.forEach((e) => {
    adj.get(e.sid)?.push(e.tid);
  });

  nodes.forEach((s) => {
    const stack: string[] = [];
    const preds = new Map<string, string[]>();
    const sigma = new Map<string, number>();
    const dist = new Map<string, number>();

    nodes.forEach((v) => {
      preds.set(v, []);
      sigma.set(v, 0);
      dist.set(v, -1);
    });
    sigma.set(s, 1);
    dist.set(s, 0);

    const queue: string[] = [s];
    while (queue.length) {
      const v = queue.shift()!;
      stack.push(v);
      (adj.get(v) || []).forEach((w) => {
        if (dist.get(w)! < 0) {
          dist.set(w, dist.get(v)! + 1);
          queue.push(w);
        }
        if (dist.get(w) === dist.get(v)! + 1) {
          sigma.set(w, sigma.get(w)! + sigma.get(v)!);
          preds.get(w)!.push(v);
        }
      });
    }

    const delta = new Map<string, number>();
    nodes.forEach((v) => delta.set(v, 0));
    while (stack.length) {
      const w = stack.pop()!;
      preds.get(w)!.forEach((v) => {
        const c = (sigma.get(v)! / sigma.get(w)!) * (1 + delta.get(w)!);
        delta.set(v, delta.get(v)! + c);
      });
      if (w !== s) {
        const m = metrics.get(w);
        if (m) m.betweenness += delta.get(w)!;
      }
    }
  });
}

function computeCloseness(nodes: string[], edges: Edge[], metrics: Map<string, MetricRow>) {
  const adj = new Map<string, string[]>();
  nodes.forEach((n) => adj.set(n, []));
  edges.forEach((e) => {
    adj.get(e.sid)?.push(e.tid);
  });
  nodes.forEach((s) => {
    const dist = new Map<string, number>();
    nodes.forEach((n) => dist.set(n, -1));
    dist.set(s, 0);
    const queue: string[] = [s];
    while (queue.length) {
      const v = queue.shift()!;
      const dv = dist.get(v)!;
      (adj.get(v) || []).forEach((w) => {
        if (dist.get(w)! < 0) {
          dist.set(w, dv + 1);
          queue.push(w);
        }
      });
    }
    let reach = 0;
    let sum = 0;
    dist.forEach((d) => {
      if (d > 0) {
        reach += 1;
        sum += d;
      }
    });
    const m = metrics.get(s);
    if (m && sum > 0) {
      m.closeness = (reach > 0 ? reach / sum : 0);
    }
  });
}

export default function NetworkDetails() {
  const { id: surveyId, source, target } = useParams<{ id: string; source: string; target: string }>();
  const { rawNomEdges, nodes, surveyJson, dimensions, howChoiceOrder } = useNetworkData({ surveyId, source, target });
  const [mode, setMode] = useState<Mode>("perHow");
  const [selectedRespondent, setSelectedRespondent] = useState<number | "ALL">("ALL");
  const [sortKey, setSortKey] = useState<SortKey>("degree");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [legendCollapsed, setLegendCollapsed] = useState<boolean>(true);
  const [selectedDim, setSelectedDim] = useState<string>(DIM_COMBINED);
  const surveyTitle = useMemo(() => (surveyJson?.title ? String(surveyJson.title) : surveyId), [surveyJson, surveyId]);
  const sourceEl = useMemo(() => findElementByName(surveyJson, source || ""), [surveyJson, source]);
  const targetEl = useMemo(() => findElementByName(surveyJson, target || ""), [surveyJson, target]);
  const sourceGroup = useMemo(() => findNetworkQuestionGroup(surveyJson, source), [surveyJson, source]);
  const targetGroup = useMemo(() => findNetworkQuestionGroup(surveyJson, target), [surveyJson, target]);
  const sourceLabel = sourceGroup ? `${sourceGroup.title} — all countries` : sourceEl?.title || friendlyLabel(source);
  const targetLabel = targetGroup ? `${targetGroup.title} — all countries` : targetEl?.title || friendlyLabel(target);

  const respondents = useMemo(
    () => Array.from(new Set(rawNomEdges.map((e) => e.respIndex))).sort((a, b) => a - b),
    [rawNomEdges]
  );

  const filteredRaw = useMemo(() => {
    if (selectedRespondent === "ALL") return rawNomEdges;
    return rawNomEdges.filter((e) => e.respIndex === selectedRespondent);
  }, [rawNomEdges, selectedRespondent]);

  const howMeta = useMemo(() => {
    const set = new Set<string>();
    let hasUnlabeled = false;
    filteredRaw.forEach((e) => {
      const vals = projectNominationValues(e, selectedDim);
      if (vals.length) vals.forEach((h) => set.add(h));
      else hasUnlabeled = true;
    });
    return { list: Array.from(set), hasUnlabeled };
  }, [filteredRaw, selectedDim]);

  const orderedChoiceValues = useMemo(() => {
    if (selectedDim === DIM_COMBINED) {
      const combined: string[] = [];
      Object.values(howChoiceOrder || {}).forEach((arr) => {
        arr.forEach((v) => {
          if (!combined.includes(v)) combined.push(v);
        });
      });
      return combined;
    }
    return howChoiceOrder?.[selectedDim] ?? [];
  }, [howChoiceOrder, selectedDim]);

  const howOptions = useMemo(() => {
    const vals = new Set(howMeta.list);
    const ordered: string[] = [];
    const seen = new Set<string>();
    orderedChoiceValues.forEach((v) => {
      if (vals.has(v) && !seen.has(v)) {
        ordered.push(v);
        seen.add(v);
      }
    });
    howMeta.list.forEach((v) => {
      if (!seen.has(v)) {
        ordered.push(v);
        seen.add(v);
      }
    });
    if (howMeta.hasUnlabeled) ordered.push(HOW_UNLABELED);
    return ordered;
  }, [howMeta, orderedChoiceValues]);

  const [selectedHows, setSelectedHows] = useState<string[]>([]);

  useEffect(() => {
    const allowed = new Set(howOptions);
    setSelectedHows((prev) => {
      const filtered = prev.filter((h) => allowed.has(h));
      return filtered.length ? filtered : Array.from(allowed);
    });
  }, [howOptions]);

  useEffect(() => {
    if (!dimensions.length) return;
    const hasSelected = dimensions.some((d) => d.name === selectedDim);
    if (!hasSelected) setSelectedDim(DIM_COMBINED);
  }, [dimensions, selectedDim]);


  const allowedHows = useMemo(() => new Set(selectedHows), [selectedHows]);

  const howScale = useMemo(() => buildEdgeColorScale(howOptions), [howOptions]);

  const { edges, nodes: nodeList } = useMemo(
    () => computeEdges(filteredRaw, mode, allowedHows, selectedDim),
    [filteredRaw, mode, allowedHows, selectedDim]
  );
  const metricsMap = useMemo(() => {
    const m = computeDegrees(nodeList, edges);
    computeBetweenness(nodeList, edges, m);
    computeCloseness(nodeList, edges, m);
    return m;
  }, [nodeList, edges]);

  const rows = useMemo(() => Array.from(metricsMap.values()), [metricsMap]);
  const sortedRows = useMemo(() => {
    const sorted = [...rows].sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortKey === "id") return a.id.localeCompare(b.id) * dir;
      return ((a[sortKey] as number) - (b[sortKey] as number)) * dir;
    });
    return sorted;
  }, [rows, sortKey, sortDir]);

  if (!surveyId || !source || !target) {
    return <Alert severity="error">Missing route parameters.</Alert>;
  }

  if (!rawNomEdges.length || !nodes.length) {
    return (
      <Box sx={{ p: 3, display: "flex", gap: 1, alignItems: "center" }}>
        <CircularProgress size={20} />
        <Typography>Loading network data…</Typography>
      </Box>
    );
  }

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "id" ? "asc" : "desc");
    }
  };

  const toggleHow = (label: string) => {
    if (mode !== "perHow") return;
    setSelectedHows((prev) => (prev.includes(label) ? prev.filter((h) => h !== label) : [...prev, label]));
  };

  return (
    <Box sx={{ px: 3, pt: 1, pb: 3, display: "flex", flexDirection: "column", gap: 2, position: "relative" }}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Network Details
          </Typography>
          <Typography variant="body2" color="text.secondary">{`Survey: ${surveyTitle}`}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <FormControl size="small">
            <InputLabel id="mode-label">Mode</InputLabel>
            <Select
              labelId="mode-label"
              label="Mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as Mode)}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="perHow">Per-Type</MenuItem>
              <MenuItem value="aggregate">Aggregate</MenuItem>
              <MenuItem value="rawNomination">Raw (per nomination)</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="resp-label">Respondent</InputLabel>
            <Select
              labelId="resp-label"
              label="Respondent"
              value={selectedRespondent as any}
              onChange={(e) => setSelectedRespondent(e.target.value as any)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="ALL">All respondents</MenuItem>
              {respondents.map((r) => (
                <MenuItem key={r} value={r}>
                  Respondent {r + 1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            component={RouterLink}
            to={`/network/${surveyId}/${source}/${target}`}
            variant="outlined"
          >
            View graph
          </Button>
        </Stack>
      </Stack>
      <Paper
        elevation={2}
        sx={{
          width: legendCollapsed ? 360 : 560,
          maxWidth: "100%",
          p: legendCollapsed ? 1 : 1.5,
          opacity: mode === "perHow" ? 1 : 0.5,
          userSelect: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Relationship type (edge colors)
          </Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setLegendCollapsed((v) => !v)}>
            {legendCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Dimension
          </Typography>
          <Select
            size="small"
            value={selectedDim}
            onChange={(e) => setSelectedDim(e.target.value)}
            sx={{ minWidth: 160, bgcolor: "#fff" }}
            disabled={mode !== "perHow"}
          >
            {dimensions.map((d) => (
              <MenuItem key={d.name} value={d.name}>
                {d.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {!legendCollapsed && (
          <>
            <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }}>
              <Button size="small" onClick={() => setSelectedHows(howOptions)} disabled={mode !== "perHow"}>
                All
              </Button>
              <Button size="small" onClick={() => setSelectedHows([])} disabled={mode !== "perHow"}>
                None
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ maxHeight: 260, overflowY: "auto" }}>
              {howOptions.map((h) => {
                const active = selectedHows.includes(h);
                const color = howScale(h) as string;
                return (
                  <Chip
                    key={h}
                    size="small"
                    label={h}
                    onClick={() => toggleHow(h)}
                    sx={{
                      mb: 1,
                      borderColor: active ? "#666" : "#ddd",
                      borderWidth: 1,
                      borderStyle: "solid",
                      opacity: active ? 1 : 0.3,
                      color: "#111",
                      backgroundColor: color ?? "#bfc5ca",
                      cursor: mode === "perHow" ? "pointer" : "not-allowed",
                    }}
                  />
                );
              })}
            </Stack>
            {mode !== "perHow" && (
              <Typography variant="caption" color="text.secondary">
                Available in Per-Type mode.
              </Typography>
            )}
          </>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography variant="overline" color="text.secondary">
              Source
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {sourceLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {source}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="overline" color="text.secondary">
              Target
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {targetLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {target}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Node metrics ({rows.length} nodes)
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                { key: "id", label: "Node", hint: "Node identifier" },
                { key: "inDegree", label: "In-degree", hint: "Count of incoming edges" },
                { key: "outDegree", label: "Out-degree", hint: "Count of outgoing edges" },
                { key: "degree", label: "Total degree", hint: "In-degree + Out-degree" },
                { key: "betweenness", label: "Betweenness", hint: "How often this node sits on shortest paths between others" },
                { key: "closeness", label: "Closeness", hint: "Reciprocal of the average distance to all reachable nodes" },
              ].map((col) => (
                <TableCell key={col.key}>
                  <Stack direction="row" spacing={0.3} alignItems="center">
                    <TableSortLabel
                      active={sortKey === col.key}
                      direction={sortKey === col.key ? sortDir : "asc"}
                      onClick={() => handleSort(col.key as SortKey)}
                    >
                      {col.label}
                    </TableSortLabel>
                    {col.hint ? (
                      <Tooltip title={col.hint} placement="top">
                        <IconButton size="small" sx={{ p: 0.2 }}>
                          <InfoOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.id}</TableCell>
                <TableCell>{row.inDegree}</TableCell>
                <TableCell>{row.outDegree}</TableCell>
                <TableCell>{row.degree}</TableCell>
                <TableCell>{row.betweenness.toFixed(2)}</TableCell>
                <TableCell>{row.closeness.toFixed(3)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
